// Google Workspace card - Gmail (own OAuth probe), Drive and Calendar
// tiles fed by GET /api/firm/workspace. Every claim is a server answer:
// needsAuth renders a connect path, an unreachable server renders
// "Not verified", and nothing invents documents, events or sync ages.

import { type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { GIcon } from "../chrome/GIcon";
import { fetchGmailThreads, timeAgo } from "../../api/mock";
import { useIntegrationsStatus } from "../../hooks/useIntegrationsStatus";
import { siteHostOf, useFirmIdentity } from "../../api/attorneyConfig";
import { SEP } from "../chrome/SepList";
import { useGoogleConnect } from "../../hooks/useGoogleConnect";
import { useFirmWorkspace } from "../../hooks/useFirmWorkspace";

// Stable destinations for the "Open" links. New tab so the user doesn't
// lose dashboard state.
const GMAIL_INBOX_URL = "https://mail.google.com/";
const DRIVE_HOME_URL = "https://drive.google.com/";
const CALENDAR_URL = "https://calendar.google.com/";
const MEET_NEW_URL = "https://meet.google.com/new";

const ARROW = String.fromCharCode(8594);

function driveFileUrl(fileId: string): string {
  return `https://drive.google.com/file/d/${encodeURIComponent(fileId)}/view`;
}

function eventTime(start: string): string {
  // An all-day event arrives as a bare date from the Calendar API.
  if (start.length <= 10) return "All day";
  return new Date(start).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });
}

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function GmailTile() {
  // The connection verdict comes from the integrations aggregate - the
  // same answer the TopBar pill and /settings/integrations render, so
  // this tile can never disagree with them. A disconnect invalidates
  // ["integrations","status"] and the Live pill drops without a remount.
  const statusQ = useIntegrationsStatus();
  const gmailRow = statusQ.data?.find((p) => p.id === "gmail");
  const connected = gmailRow?.state === "connected";
  const threadsQ = useQuery({
    queryKey: ["gmail", "threads"],
    queryFn: () => fetchGmailThreads(3),
    enabled: connected,
    staleTime: 60_000,
    retry: false,
  });
  const threads = threadsQ.data?.threads ?? [];

  if (statusQ.isPending) {
    return (
      <div className="cc-ws-app">
        <div className="cc-ws-app-head">
          <GIcon kind="gmail" />
          <span className="name">Gmail</span>
        </div>
        <div className="v2-gmail-status">checking connection…</div>
      </div>
    );
  }

  // An unreachable aggregate is not a disconnected Gmail - claim nothing
  // and offer nothing, exactly like the Drive and Calendar tiles.
  if (statusQ.isError || !gmailRow) {
    return (
      <div className="cc-ws-app">
        <div className="cc-ws-app-head">
          <GIcon kind="gmail" />
          <span className="name">Gmail</span>
          <span className="v2-status-pill unknown">
            <span className="dot" />
            Not verified
          </span>
        </div>
        <div className="cc-ws-app-line" style={{ padding: "8px 0", color: "var(--muted)" }}>
          Could not verify the Gmail connection.
        </div>
      </div>
    );
  }

  if (!connected) {
    return (
      <div className="cc-ws-app">
        <div className="cc-ws-app-head">
          <GIcon kind="gmail" />
          <span className="name">Gmail</span>
          <span className="v2-status-pill action">
            <span className="dot" />
            Action required
          </span>
        </div>
        <div className="cc-ws-app-line" style={{ padding: "8px 0", color: "var(--muted)" }}>
          Connect Google to surface client + agency threads inline.
        </div>
        <div className="cc-ws-app-foot">
          {/* The one consent covers Gmail, Calendar and Contacts - a
              "Connect Gmail" label would lie about the action's scope. */}
          <ConnectGoogleButton label="Connect Google" fallbackError={gmailRow.detail} />
        </div>
      </div>
    );
  }

  return (
    <div className="cc-ws-app">
      <div className="cc-ws-app-head">
        <GIcon kind="gmail" />
        <span className="name">Gmail</span>
        <span className="v2-status-pill live">
          <span className="dot" />
          Live
        </span>
        <span className="badge alert">{threads.length || ""}</span>
      </div>
      {threads.slice(0, 3).map((t, i) => (
        <div key={i} className="cc-ws-app-line">
          <span className="who-av" style={{ background: "var(--ink)" }}>
            {(t.from || "??").slice(0, 1).toUpperCase()}
          </span>
          <span className="who-name">{(t.from || "(unknown)").split("@")[0].slice(0, 12)}</span>
          <span className="ttl">· {(t.subject || "(no subject)").slice(0, 36)}</span>
          <span className="ago">{t.ago || "—"}</span>
        </div>
      ))}
      <div className="cc-ws-app-foot">
        <span className="v2-gmail-status ok">Synced from {gmailRow.account || "Gmail"}</span>
        <a
          className="open"
          href={GMAIL_INBOX_URL}
          target="_blank"
          rel="noreferrer noopener"
          title="Open Gmail in a new tab"
        >
          Open inbox →
        </a>
      </div>
    </div>
  );
}

// The one in-app path to the Google consent flow, shared by every tile
// that can ask for the grant.
function ConnectGoogleButton({ label, fallbackError }: { label: string; fallbackError?: string }) {
  const google = useGoogleConnect();
  return (
    <>
      <button className="cc-link-btn" onClick={google.connect} disabled={google.connecting}>
        {google.connecting ? "Opening Google..." : label}
      </button>
      <span className="v2-gmail-status error" style={{ marginLeft: "auto" }}>
        {google.error ?? fallbackError ?? ""}
      </span>
    </>
  );
}

type WorkspaceQuery = ReturnType<typeof useFirmWorkspace>;

/** Head pill: the server's verdict or, without one, "Not verified". */
function TileStatusPill({ q, needsAuth }: { q: WorkspaceQuery; needsAuth: boolean | undefined }) {
  if (q.isLoading) return null;
  if (needsAuth === undefined) {
    return (
      <span className="v2-status-pill unknown">
        <span className="dot" />
        Not verified
      </span>
    );
  }
  return needsAuth ? (
    <span className="v2-status-pill action">
      <span className="dot" />
      Not connected
    </span>
  ) : (
    <span className="v2-status-pill live">
      <span className="dot" />
      Live
    </span>
  );
}

function MutedLine({ children }: { children: ReactNode }) {
  return (
    <div className="cc-ws-app-line" style={{ padding: "8px 0", color: "var(--muted)" }}>
      {children}
    </div>
  );
}

function DriveTile({ q }: { q: WorkspaceQuery }) {
  const drive = q.data?.drive;
  return (
    <div className="cc-ws-app">
      <div className="cc-ws-app-head">
        <GIcon kind="drive" />
        <span className="name">Drive</span>
        <TileStatusPill q={q} needsAuth={drive?.needsAuth} />
        {drive && !drive.needsAuth && (
          <span className="badge soft">{drive.touchedToday} today</span>
        )}
      </div>
      {q.isLoading && <div className="v2-gmail-status">checking connection...</div>}
      {!q.isLoading && !drive && <MutedLine>Could not reach the server.</MutedLine>}
      {drive?.needsAuth && (
        // No diagnosis: the server only says the section is dark, not why
        // (missing key, unshared Shared Drive, quota...). Name the path.
        <MutedLine>
          Drive is not connected - the service-account link is set up server-side.
        </MutedLine>
      )}
      {drive && !drive.needsAuth && drive.recent.length === 0 && (
        <MutedLine>Nothing modified recently.</MutedLine>
      )}
      {drive &&
        !drive.needsAuth &&
        drive.recent.slice(0, 3).map((f) => (
          <a
            key={f.fileId}
            className="cc-ws-app-line"
            href={driveFileUrl(f.fileId)}
            target="_blank"
            rel="noreferrer noopener"
            style={{ textDecoration: "none", color: "inherit" }}
          >
            <GIcon kind="docs" size={12} />
            <span className="ttl" style={{ color: "var(--ink)" }}>
              {f.name}
            </span>
            <span className="ago">{timeAgo(f.modifiedTime) ?? ""}</span>
          </a>
        ))}
      <div className="cc-ws-app-foot">
        <a
          className="open"
          href={DRIVE_HOME_URL}
          target="_blank"
          rel="noreferrer noopener"
          title="Open Drive in a new tab"
          style={{ marginLeft: "auto" }}
        >
          Open Drive {ARROW}
        </a>
      </div>
    </div>
  );
}

function CalendarTile({ q }: { q: WorkspaceQuery }) {
  const cal = q.data?.calendar;
  return (
    <div className="cc-ws-app">
      <div className="cc-ws-app-head">
        <GIcon kind="calendar" />
        <span className="name">Calendar</span>
        <TileStatusPill q={q} needsAuth={cal?.needsAuth} />
        {cal && !cal.needsAuth && <span className="badge soft">{cal.today.length} today</span>}
      </div>
      {q.isLoading && <div className="v2-gmail-status">checking connection...</div>}
      {!q.isLoading && !cal && <MutedLine>Could not reach the server.</MutedLine>}
      {cal?.needsAuth && <MutedLine>Connect Google to see the firm calendar here.</MutedLine>}
      {cal && !cal.needsAuth && cal.today.length === 0 && (
        <MutedLine>Nothing on the calendar today.</MutedLine>
      )}
      {cal &&
        !cal.needsAuth &&
        cal.today.slice(0, 3).map((e) => (
          <div key={e.id} className="cc-ws-app-line" title={e.location ?? ""}>
            <span className="ttl" style={{ color: "var(--ink)" }}>
              {e.summary}
            </span>
            <span className="ago">{eventTime(e.start)}</span>
          </div>
        ))}
      {/* The next hearing comes from the case files, so it is real even
          while the calendar itself still needs a grant. */}
      {cal?.nextHearing && (
        <div className="cc-ws-app-line" title={cal.nextHearing.location ?? ""}>
          <span className="who-name">Next hearing</span>
          <span className="ttl">{cal.nextHearing.summary}</span>
          <span className="ago">{shortDate(cal.nextHearing.start)}</span>
        </div>
      )}
      <div className="cc-ws-app-foot">
        {cal?.needsAuth ? (
          <ConnectGoogleButton label="Connect Google" />
        ) : (
          <a
            className="cc-meet-btn"
            href={MEET_NEW_URL}
            target="_blank"
            rel="noreferrer noopener"
            title="Start a Google Meet in a new tab"
            style={{ textDecoration: "none" }}
          >
            <GIcon kind="meet" size={11} /> Start huddle
          </a>
        )}
        <a
          className="open"
          href={CALENDAR_URL}
          target="_blank"
          rel="noreferrer noopener"
          title="Open Calendar in a new tab"
          style={{ marginLeft: "auto" }}
        >
          Open {ARROW}
        </a>
      </div>
    </div>
  );
}

export function WorkspaceCard() {
  // The real firm domain or nothing - never a fixture, never a made-up
  // sync age.
  const siteHost = siteHostOf(useFirmIdentity().data?.website);
  const ws = useFirmWorkspace();
  return (
    <div className="cc-card">
      <div className="cc-card-head">
        <div className="cc-card-title">Google Workspace</div>
        {siteHost && (
          <div className="cc-card-sub">
            {SEP} {siteHost}
          </div>
        )}
      </div>
      <div className="cc-workspace">
        <GmailTile />
        <DriveTile q={ws} />
        <CalendarTile q={ws} />
      </div>
    </div>
  );
}
